import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, X } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePrincipals } from '@/hooks/usePrincipals';
import { formatRelativeDate } from '@/lib/formatters';
import type { ListPrincipalsParams } from '@/api/principals';
import type { Principal } from '@/types/principal';
import { AddPrincipalDialog } from './AddPrincipalDialog';

const PER_PAGE = 25;
const MAX_GROUP_CHIPS = 3;

type StatusFilter = 'all' | 'active' | 'inactive' | 'suspended';

interface UsersTabProps {
  groupFilter: string | null;
  onClearGroupFilter: () => void;
  onSelectPrincipal: (principal: Principal) => void;
}

/** Users tab — every provisioned principal, deactivated ones included. */
export function UsersTab({ groupFilter, onClearGroupFilter, onSelectPrincipal }: UsersTabProps) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Default "all": deactivated users must be visible — offboarding evidence.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => setPage(1), [search, statusFilter, groupFilter]);

  const params: ListPrincipalsParams = {
    page,
    per_page: PER_PAGE,
    status: statusFilter,
    search: search || undefined,
    group: groupFilter || undefined,
  };
  const { data, isLoading } = usePrincipals(params);
  const rows = data?.data ?? [];
  const pagination = data?.meta.pagination;
  const hasFilters = Boolean(search || groupFilter || statusFilter !== 'all');

  const columns: Column<Principal>[] = [
    {
      key: 'user',
      header: 'User',
      render: (p) => (
        <div className="min-w-0">
          <div className="font-medium text-foreground truncate">{p.display_name}</div>
          <div className="text-xs text-muted-foreground truncate">{p.email || '—'}</div>
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (p) => p.identity_provider_name || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusBadge status={p.status} />,
    },
    {
      key: 'groups',
      header: 'Groups',
      render: (p) => {
        const groups = p.groups ?? [];
        if (groups.length === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {groups.slice(0, MAX_GROUP_CHIPS).map((g) => (
              <Badge key={g} variant="secondary" className="font-normal">
                {g}
              </Badge>
            ))}
            {groups.length > MAX_GROUP_CHIPS && (
              <span className="text-xs text-muted-foreground">
                +{groups.length - MAX_GROUP_CHIPS}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      render: (p) => (p.last_seen ? formatRelativeDate(p.last_seen) : '—'),
    },
  ];

  return (
    <div className="space-y-4">
      <AddPrincipalDialog open={addOpen} onOpenChange={setAddOpen} />
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Deactivated</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        {groupFilter && (
          <Badge variant="secondary" className="gap-1 pr-1">
            Group: {groupFilter}
            <button
              type="button"
              aria-label={`Clear group filter ${groupFilter}`}
              onClick={onClearGroupFilter}
              className="ml-1 rounded-full p-0.5 hover:bg-accent"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        <Button className="ml-auto" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />Add user
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 && !hasFilters ? (
        <EmptyState
          icon={<Users className="h-12 w-12" />}
          title="No users yet"
          description="Add users directly to your local directory on any plan, or connect an identity provider (Growth and above) to sync them."
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />Add user
              </Button>
              <Button asChild variant="outline">
                <Link to="/identity-providers">Connect an identity provider</Link>
              </Button>
            </div>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(p) => p.id}
          onRowClick={onSelectPrincipal}
          emptyMessage="No users match the current filters."
        />
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {rows.length} of {pagination.total} · page {pagination.page} of{' '}
            {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
